import { useRef } from "react";
import type { MutableRefObject, RefObject } from "react";

import { ANCHOR_TOP_GAP_PX } from "./useScrollBehavior";

/** The last message's bottom within this of the view's bottom = the customer is following. */
const FOLLOW_SLOP_PX = 80;
/** Room left under a rep reply that fits. */
const BOTTOM_PAD_PX = 16;
/** How long a reveal glides; a second reply inside it still counts as followed. */
const REVEAL_MS = 1200;

export type RepReveal = "reveal" | "pill" | null;

/** The parts of useScrollBehavior this needs (passed in, so this file stays off the phone surface). */
export type RepReplyRevealDeps = {
  embedScrollRef: RefObject<HTMLElement | null>;
  lastAssistantMsgRef: RefObject<HTMLElement | null>;
  lastUserMsgRef: RefObject<HTMLElement | null>;
  isProgrammaticScrollRef: MutableRefObject<boolean>;
  isPhoneTurn: () => boolean;
  getElementScrollOffset: (el: HTMLElement) => number;
  doScrollTo: (options: ScrollToOptions) => void;
  scheduleProgrammaticScrollEnd: (delayMs: number) => void;
  setShowScrollButton: (show: boolean) => void;
};

/**
 * TRACI-256: a live rep's reply arrives on its own timing, wherever the customer left
 * the view, and the send-time anchor had left auto-scroll off, so a reply that landed
 * below the fold stayed hidden. Desktop only: on a phone, settleScroll (TRAC-1132)
 * owns every scroll and nothing here runs.
 *
 * noteRepReply() runs BEFORE the reply is added. A customer following the end of the
 * chat (the bottom of the last message on screen, judged without the anchor spacer,
 * or a reveal still gliding) gets the reply brought on screen; one scrolled up reading
 * gets the scroll-down button instead. The messages effect claims the request on every
 * run, before its early returns, so a skipped one can never fire on a later render.
 */
export function useRepReplyReveal(deps: RepReplyRevealDeps) {
  const pendingRef = useRef<RepReveal>(null);
  const revealUntilRef = useRef(0);

  const noteRepReply = (): void => {
    const c = deps.embedScrollRef.current;
    if (!c || deps.isPhoneTurn()) return;
    const bottomOf = (el: HTMLElement | null): number =>
      el ? deps.getElementScrollOffset(el) + el.getBoundingClientRect().height : 0;
    const tail = Math.max(bottomOf(deps.lastAssistantMsgRef.current), bottomOf(deps.lastUserMsgRef.current));
    // No message drawn yet to judge by (a restore still landing): don't move the view.
    const following = Date.now() < revealUntilRef.current
      || (tail > 0 && tail <= c.scrollTop + c.clientHeight + FOLLOW_SLOP_PX);
    pendingRef.current = following ? "reveal" : "pill";
  };

  const claimRepReveal = (): RepReveal => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    return pending;
  };

  /** Shows as much of the reply as fits, never scrolling its first line off the top, never up. */
  const revealRepReply = (pending: Exclude<RepReveal, null>): void => {
    if (pending === "pill") {
      deps.setShowScrollButton(true);
      return;
    }
    revealUntilRef.current = Date.now() + REVEAL_MS;
    requestAnimationFrame(() => {
      const c = deps.embedScrollRef.current;
      const reply = deps.lastAssistantMsgRef.current;
      if (!c || !reply) return;
      const top = deps.getElementScrollOffset(reply);
      const bottom = top + reply.getBoundingClientRect().height;
      const target = Math.max(0, Math.min(top - ANCHOR_TOP_GAP_PX, bottom + BOTTOM_PAD_PX - c.clientHeight));
      if (target <= c.scrollTop + 1) return;
      deps.isProgrammaticScrollRef.current = true;
      deps.doScrollTo({ top: target, behavior: "smooth" });
      deps.scheduleProgrammaticScrollEnd(1000);
    });
  };

  return { noteRepReply, claimRepReveal, revealRepReply };
}
